import React, { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import styled from 'styled-components';
import { useField } from 'formik';
import {
  Alert,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  TextArea,
  TextInput,
  Select,
  SelectOption,
  SelectList,
  MenuToggle,
} from '@patternfly/react-core';
import { required } from 'util/validators';

import { FormColumnLayout, FormFullWidthLayout } from 'components/FormLayout';
import Popover from 'components/Popover';
import AnsibleSelect from 'components/AnsibleSelect';
import FormField from 'components/FormField';
import getDocsBaseUrl from 'util/getDocsBaseUrl';
import { useConfig, useUserProfile } from 'contexts/Config';
import InventorySourcesList from './InventorySourcesList';
import JobTemplatesList from './JobTemplatesList';
import ProjectsList from './ProjectsList';
import SystemJobTemplatesList from './SystemJobTemplatesList';
import WorkflowJobTemplatesList from './WorkflowJobTemplatesList';

const NodeTypeErrorAlert = styled(Alert)`
  margin-bottom: 20px;
`;

const TimeoutInput = styled(TextInput)`
  width: 200px !important;
  :not(:first-of-type) {
    margin-left: 20px;
  }
`;
TimeoutInput.displayName = 'TimeoutInput';

const TimeoutLabel = styled.p`
  margin-left: 10px;
  min-width: fit-content;
`;

function NodeTypeStep({ isIdentifierRequired }) {
  const { t } = useLingui();
  const { isSuperUser } = useUserProfile();
  const [nodeTypeField, , nodeTypeHelpers] = useField('nodeType');
  const [nodeResourceField, nodeResourceMeta, nodeResourceHelpers] =
    useField('nodeResource');
  const [, approvalNameMeta, approvalNameHelpers] = useField('approvalName');
  const [, , approvalDescriptionHelpers] = useField('approvalDescription');
  const [timeoutMinutesField, , timeoutMinutesHelpers] =
    useField('timeoutMinutes');
  const [timeoutSecondsField, , timeoutSecondsHelpers] =
    useField('timeoutSeconds');
  const [convergenceField, , convergenceFieldHelpers] = useField('convergence');
  const [contextTemplateField, , contextTemplateHelpers] =
    useField('contextTemplate');
  const [requiredApprovalsField, , requiredApprovalsHelpers] =
    useField('requiredApprovals');
  const [onTimeoutField, , onTimeoutHelpers] = useField('onTimeout');

  const [isConvergenceOpen, setIsConvergenceOpen] = useState(false);
  const config = useConfig();

  const isValid = !approvalNameMeta.touched || !approvalNameMeta.error;
  const nodeTypeChoices = [
    {
      key: 'workflow_approval_template',
      value: 'workflow_approval_template',
      label: t`Approval`,
      isDisabled: false,
    },
    {
      key: 'inventory_source',
      value: 'inventory_source',
      label: t`Inventory Source Sync`,
      isDisabled: false,
    },
    {
      key: 'job_template',
      value: 'job_template',
      label: t`Job Template`,
      isDisabled: false,
    },
    {
      key: 'project',
      value: 'project',
      label: t`Project Sync`,
      isDisabled: false,
    },
    {
      key: 'workflow_job_template',
      value: 'workflow_job_template',
      label: t`Workflow Job Template`,
      isDisabled: false,
    },
  ];

  const modifiedNodeTypeChoices = isSuperUser
    ? [
        ...nodeTypeChoices,
        {
          key: 'system_job_template',
          value: 'system_job_template',
          label: t`Management Job`,
          isDisabled: false,
        },
      ]
    : nodeTypeChoices;

  return (
    <>
      {nodeResourceMeta.error && (
        <NodeTypeErrorAlert
          variant="danger"
          isInline
          title={nodeResourceMeta.error}
        />
      )}
      <div css="display: flex; align-items: center; margin-bottom: 20px;">
        <b css="margin-right: 24px">{t`Node Type`}</b>
        <div>
          <AnsibleSelect
            id="nodeResource-select"
            label={t`Select a Node Type`}
            data={modifiedNodeTypeChoices}
            value={nodeTypeField.value}
            onChange={(e, val) => {
              nodeTypeHelpers.setValue(val);
              nodeResourceHelpers.setValue(null);
              approvalNameHelpers.setValue('');
              approvalDescriptionHelpers.setValue('');
              timeoutMinutesHelpers.setValue(0);
              timeoutSecondsHelpers.setValue(0);
              contextTemplateHelpers.setValue('');
              requiredApprovalsHelpers.setValue(1);
              onTimeoutHelpers.setValue('deny');
              convergenceFieldHelpers.setValue('any');
            }}
          />
        </div>
      </div>
      {nodeTypeField.value === 'job_template' && (
        <JobTemplatesList
          nodeResource={nodeResourceField.value}
          onUpdateNodeResource={nodeResourceHelpers.setValue}
        />
      )}
      {nodeTypeField.value === 'project' && (
        <ProjectsList
          nodeResource={nodeResourceField.value}
          onUpdateNodeResource={nodeResourceHelpers.setValue}
        />
      )}
      {nodeTypeField.value === 'inventory_source' && (
        <InventorySourcesList
          nodeResource={nodeResourceField.value}
          onUpdateNodeResource={nodeResourceHelpers.setValue}
        />
      )}
      {nodeTypeField.value === 'system_job_template' && (
        <SystemJobTemplatesList
          nodeResource={nodeResourceField.value}
          onUpdateNodeResource={nodeResourceHelpers.setValue}
        />
      )}
      {nodeTypeField.value === 'workflow_job_template' && (
        <WorkflowJobTemplatesList
          nodeResource={nodeResourceField.value}
          onUpdateNodeResource={nodeResourceHelpers.setValue}
        />
      )}
      <Form autoComplete="off" css="margin-top: 20px;">
        <FormColumnLayout>
          <FormFullWidthLayout>
            {nodeTypeField.value === 'workflow_approval_template' && (
              <>
                <FormField
                  name="approvalName"
                  id="approval-name"
                  isRequired
                  validate={required(null)}
                  validated={isValid ? 'default' : 'error'}
                  label={t`Name`}
                />
                <FormField
                  name="approvalDescription"
                  id="approval-description"
                  label={t`Description`}
                />
                <FormGroup
                  label={t`Timeout`}
                  fieldId="approval-timeout"
                  name="timeout"
                >
                  <div css="display: flex;align-items: center;">
                    <TimeoutInput
                      {...timeoutMinutesField}
                      aria-label={t`Timeout minutes`}
                      id="approval-timeout-minutes"
                      min="0"
                      onChange={(event) => {
                        timeoutMinutesField.onChange(event);
                      }}
                      step="1"
                      type="number"
                    />
                    <TimeoutLabel>
                      <Trans>min</Trans>
                    </TimeoutLabel>
                    <TimeoutInput
                      {...timeoutSecondsField}
                      aria-label={t`Timeout seconds`}
                      id="approval-timeout-seconds"
                      min="0"
                      onChange={(event) => {
                        timeoutSecondsField.onChange(event);
                      }}
                      step="1"
                      type="number"
                    />
                    <TimeoutLabel>
                      <Trans>sec</Trans>
                    </TimeoutLabel>
                  </div>
                </FormGroup>
                <FormGroup
                  label={t`On timeout`}
                  fieldId="approval-on-timeout"
                  labelHelp={
                    <Popover
                      content={t`Whether the approval node is automatically approved or denied when the timeout expires.`}
                    />
                  }
                >
                  <FormSelect
                    id="approval-on-timeout"
                    ouiaId="approval-on-timeout"
                    aria-label={t`On timeout`}
                    value={onTimeoutField.value}
                    onChange={(event, val) => {
                      onTimeoutHelpers.setValue(val);
                    }}
                  >
                    <FormSelectOption value="deny" label={t`Deny`} />
                    <FormSelectOption value="approve" label={t`Approve`} />
                  </FormSelect>
                </FormGroup>
                <FormGroup
                  label={t`Required approvals`}
                  fieldId="approval-required-approvals"
                  labelHelp={
                    <Popover
                      content={t`The number of distinct users that must approve before the workflow continues. A single denial always denies the node.`}
                    />
                  }
                >
                  <TimeoutInput
                    {...requiredApprovalsField}
                    aria-label={t`Required approvals`}
                    id="approval-required-approvals"
                    min="1"
                    onChange={(event) => {
                      requiredApprovalsField.onChange(event);
                    }}
                    step="1"
                    type="number"
                  />
                </FormGroup>
                <FormGroup
                  label={t`Context Template`}
                  fieldId="approval-context-template"
                  labelHelp={
                    <Popover
                      content={t`A Jinja2 template rendered with upstream set_stats artifacts when the approval is created. Use this to show the approver relevant context from previous job steps. Available variables come from set_stats data of parent nodes.`}
                    />
                  }
                >
                  <TextArea
                    {...contextTemplateField}
                    aria-label={t`Context Template`}
                    id="approval-context-template"
                    onChange={(event) => {
                      contextTemplateField.onChange(event);
                    }}
                    resizeOrientation="vertical"
                    rows={4}
                  />
                </FormGroup>
              </>
            )}
            <FormGroup
              fieldId="convergence"
              label={t`Convergence`}
              isRequired
              labelHelp={
                <Popover
                  content={
                    <>
                      {t`Preconditions for running this node when there are multiple parents. Refer to the`}{' '}
                      <a
                        href={`${getDocsBaseUrl(
                          config
                        )}/userguide/workflow_templates.html#convergence-node`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t`documentation`}
                      </a>{' '}
                      {t`for more info.`}
                    </>
                  }
                />
              }
            >
              <Select
                isOpen={isConvergenceOpen}
                onOpenChange={setIsConvergenceOpen}
                onSelect={(_event, selection) => {
                  convergenceFieldHelpers.setValue(selection);
                  setIsConvergenceOpen(false);
                }}
                aria-label={t`Convergence select`}
                className="convergenceSelect"
                data-ouia-component-id="convergenceSelect"
                toggle={(toggleRef) => (
                  <MenuToggle
                    ref={toggleRef}
                    onClick={() => setIsConvergenceOpen(!isConvergenceOpen)}
                    isExpanded={isConvergenceOpen}
                  >
                    {convergenceField.value === 'any' ? t`Any` : t`All`}
                  </MenuToggle>
                )}
              >
                <SelectList>
                  <SelectOption value="any" id="select-option-any">
                    {t`Any`}
                  </SelectOption>
                  <SelectOption value="all" id="select-option-all">
                    {t`All`}
                  </SelectOption>
                </SelectList>
              </Select>
            </FormGroup>
            <FormField
              id="node-alias"
              name="identifier"
              aria-label={t`Node Alias`}
              label={t`Node Alias`}
              tooltip={t`If specified, this field will be shown on the node instead of the resource name when viewing the workflow`}
              isRequired={isIdentifierRequired}
              validate={isIdentifierRequired ? required(null) : null}
              validated={isValid ? 'default' : 'error'}
            />
            {nodeTypeField.value !== 'workflow_approval_template' && (
              <FormField
                id="node-max-retries"
                name="maxRetries"
                type="number"
                min="0"
                max="100"
                aria-label={t`Max Retries`}
                label={t`Max Retries`}
                tooltip={t`Maximum number of times this node's job is automatically retried after failing before its failure paths are followed. Canceled jobs are never retried.`}
              />
            )}
          </FormFullWidthLayout>
        </FormColumnLayout>
      </Form>
    </>
  );
}
export default NodeTypeStep;
